import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, Copy } from "lucide-react";

export function RecoveryCodes({ codes, onContinue }) {
  return (
    <div className="space-y-3" data-testid="mfa-recovery-codes">
      <h2 className="font-bold">Save your recovery codes</h2>
      <p className="text-sm text-muted-foreground">Each code works once if you lose your phone. They won't be shown again.</p>
      <div className="grid grid-cols-2 gap-2 font-mono text-sm rounded-lg border border-border bg-muted/40 p-3">{codes.map((c) => <span key={c}>{c}</span>)}</div>
      <Button variant="outline" className="w-full" onClick={() => navigator.clipboard?.writeText(codes.join("\n"))}><Copy className="h-4 w-4 mr-1" />Copy codes</Button>
      <Button className="w-full" onClick={onContinue} data-testid="mfa-continue-btn">I've saved them — continue</Button>
    </div>
  );
}

export function MfaStep({ mfa, onDone, onBack }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (mfa.mfa_setup) api.post("/auth/mfa/setup", { mfa_token: mfa.mfa_token }).then(({ data }) => setSetup(data)).catch((ex) => setErr(apiError(ex.response?.data?.detail)));
  }, [mfa]);
  const verify = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfa.mfa_token, code });
      if (data.recovery_codes) { setCodes(data.recovery_codes); setResult(data); } else onDone(data);
    } catch (ex) { setErr(apiError(ex.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (codes) return <RecoveryCodes codes={codes} onContinue={() => onDone(result)} />;
  return (
    <form onSubmit={verify} className="space-y-4" data-testid="mfa-step">
      <div className="flex items-center gap-2 font-bold"><ShieldCheck className="h-5 w-5 text-primary" />{mfa.mfa_setup ? "Set up 2-step login" : "Enter your 6-digit code"}</div>
      {mfa.mfa_setup && (
        <div className="space-y-2 text-sm">
          <p className="text-muted-foreground">Admins must use an authenticator app. Scan this QR code with Google Authenticator, Microsoft Authenticator or similar.</p>
          {setup ? <img src={setup.qr} alt="2-step login QR code" className="h-44 w-44 mx-auto rounded-lg border border-border bg-white p-1" data-testid="mfa-qr" /> : <Loader2 className="h-6 w-6 animate-spin mx-auto" />}
          {setup && <p className="text-xs text-muted-foreground text-center">Can't scan? Enter this key: <span className="font-mono break-all" data-testid="mfa-secret">{setup.secret}</span></p>}
        </div>
      )}
      {!mfa.mfa_setup && <p className="text-sm text-muted-foreground">Open your authenticator app. You can also use a recovery code.</p>}
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Code</Label>
        <Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoFocus className="mt-1.5 font-mono tracking-widest text-center text-lg" placeholder="123456" data-testid="mfa-code-input" />
      </div>
      {err && <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2" data-testid="mfa-error">{err}</div>}
      <Button type="submit" className="w-full h-11" disabled={busy || code.length < 6} data-testid="mfa-verify-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}</Button>
      <button type="button" onClick={onBack} className="text-xs text-muted-foreground w-full" data-testid="mfa-back-btn">Back to sign in</button>
    </form>
  );
}
